import {SagaIterator} from "redux-saga";
import {Api, ApiError} from "modules/utils/Api";
import {
	addPointToScore,
	addPointToStrikes,
	clearContestPointsSuccess,
	fetchContestResultsClear,
	fetchContestResultsSuccess,
	fetchContestsSuccess,
	gameOver,
	pushPlayerToCardsArray,
	putPlayerStat,
	setActiveQuestion,
	setCurrentQuestionIndex,
	setGameStarted,
	setIsButtonsDisabled,
	setTimerTime,
	showErrorModal,
	toggleGameOverModal,
	toggleTimerVisibility,
} from "modules/actions";
import {call, delay, put, select} from "typed-redux-saga";
import {
	getActiveContest,
	getContestCorrectAnswers,
	getContestGameBar,
	getContestResult,
	getCurrentQuestion,
	getCurrentQuestionIndex,
	getIsGameStarted,
} from "modules/selectors";
import {
	IGetQuestionRequestPayload,
	ISagaAction,
	ISaveAnswerRequestPayload,
	ISaveQuestionPayload,
	IServerPrediction,
} from "modules/types";
import {ANIMATION_DELAYS, NUM_CHANCES} from "modules/constants";
import {AdobeAnalyticService} from "modules/utils/Services/AdobeAnalyticService";
import {AdobeTrackStateActions, PlayerStats} from "modules/enums";
import {getLocallyTranslatedCategory, numOrZero} from "modules/utils/helpers";
import {eq, every} from "lodash";

export const fetchContestsSaga = function* (): SagaIterator {
	try {
		const response = yield* call(Api.JSON.contests);
		ApiError.CHECK_JSON(response.data, "Error while loading contests");

		yield* put(fetchContestsSuccess(response.data.contests));
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	}
};

export const putPlayersStatsSaga = function* (payload: IServerPrediction): SagaIterator {
	try {
		yield* put(
			putPlayerStat({
				playerId: payload.playerIdOne,
				stat: payload.playerOneStats,
			})
		);
		yield* put(
			putPlayerStat({
				playerId: payload.playerIdTwo,
				stat: payload.playerTwoStats,
			})
		);
	} catch (e) {
		yield* put(showErrorModal(String(e)));
	}
};

export const gameStartSaga = function* (): SagaIterator {
	try {
		const isGameStarted = yield* select(getIsGameStarted);
		const activeContest = yield* select(getActiveContest);

		if (isGameStarted || !activeContest) {
			return;
		}

		const data: IGetQuestionRequestPayload = {
			contestId: activeContest.id,
			predictionId: 1,
		};

		const response = yield* call(Api.Contest.getQuestion, data);
		ApiError.CHECK(response.data);
		yield* call(trackNewQuestionSaga, 0);

		yield* put(setActiveQuestion(response.data.success));
		yield* put(setCurrentQuestionIndex(1));

		yield* put(pushPlayerToCardsArray(response.data.success.playerIdOne));
		yield* put(pushPlayerToCardsArray(response.data.success.playerIdTwo));
		yield* call(putPlayersStatsSaga, response.data.success);

		// False for first question
		yield* put(toggleTimerVisibility(false));
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	}
};

export const gameOverSaga = function* (): SagaIterator {
	try {
		const contest = yield* select(getActiveContest);
		if (!contest) {
			throw new ApiError("Invalid contest");
		}

		yield* delay(2000);

		// It fetches result also
		yield* call(gameOverModalAnalytics);
		yield* put(gameOver());
		yield* put(toggleGameOverModal(true));
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	}
};

export const updateStatsSaga = function* (): SagaIterator {
	try {
		const currentQuestion = yield* select(getCurrentQuestion);
		const result = yield* select(getContestResult);
		if (!result || !result.gameBar) {
			return;
		}

		if (!currentQuestion?.isCorrect) {
			yield* put(addPointToStrikes());
		} else if (currentQuestion?.isCorrect) {
			yield* put(addPointToScore());
		}
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	}
};

// Main game logic, answer game logic
export const answerQuestionSaga = function* ({
	payload,
}: ISagaAction<ISaveQuestionPayload>): SagaIterator {
	try {
		const currentQuestion = yield* select(getCurrentQuestion);
		const currentQuestionIndex = yield* select(getCurrentQuestionIndex);
		const activeContest = yield* select(getActiveContest);
		const gameBar = yield* select(getContestGameBar);
		const numberStrikes = gameBar?.numberStrikes ?? 0;

		if (!currentQuestion || !activeContest) {
			throw new ApiError("Invalid question or contest");
		}

		// Stop timer and disable buttons to prevent extra clicks
		yield* put(setIsButtonsDisabled(true));
		yield* put(toggleTimerVisibility(false));

		// Send answer to BE
		const data: ISaveAnswerRequestPayload = {
			position: currentQuestion.position,
			...payload,
		};
		const response = yield* call(Api.Contest.saveAnswer, {
			contestId: activeContest.id,
			payload: data,
		});

		ApiError.CHECK(response.data);

		// Save question with new stats to show second player stats
		yield* put(setActiveQuestion(response.data.success));
		yield* call(putPlayersStatsSaga, response.data.success);

		yield* call(answerResponseAnalyticSaga, {
			time: payload.time,
			isCorrect: response.data.success.isCorrect!,
		});

		// update stats in stats header
		yield* call(updateStatsSaga);

		// if wrong answer and reached strike limit we end the game
		if (every([!response.data.success.isCorrect, eq(numberStrikes + 1, NUM_CHANCES)])) {
			yield* call(gameOverSaga);
			return;
		}

		const getAnswerStartTime = new Date().getTime();

		// Fetch next question
		const questionData: IGetQuestionRequestPayload = {
			contestId: activeContest.id,
			predictionId: currentQuestionIndex + 1,
		};
		const newQuestionResponse = yield* call(Api.Contest.getQuestion, questionData);
		yield* call(trackNewQuestionSaga, 1);
		ApiError.CHECK(newQuestionResponse.data);
		yield* put(setGameStarted(true));
		/*
		 * Wait until animation ends (2500ms for now)
		 *  Or show next question immediately if request took too long time
		 */
		const getAnswerEndTime = new Date().getTime();
		const awaitTime = ANIMATION_DELAYS.cardToggleDelay;
		let diff = getAnswerEndTime - getAnswerStartTime;
		diff = diff > awaitTime ? 0 : diff;
		yield* delay(awaitTime - diff);

		/*
		 * Set new question as active
		 * Update players stats mapper to show current player as known, and next one as unknown
		 */
		yield* put(pushPlayerToCardsArray(newQuestionResponse.data.success.playerIdTwo));
		yield* put(setCurrentQuestionIndex(currentQuestionIndex + 1));
		yield* put(setActiveQuestion(newQuestionResponse.data.success));
		yield* call(putPlayersStatsSaga, newQuestionResponse.data.success);

		yield* put(toggleTimerVisibility(true));
		yield* put(setTimerTime(10));
		yield* put(setIsButtonsDisabled(false));
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	}
};

export const fetchContestResultsSaga = function* ({payload}: ISagaAction<number>): SagaIterator {
	try {
		const response = yield* call(Api.Contest.showMy, payload);
		ApiError.CHECK(response.data);
		yield* put(fetchContestResultsSuccess(response.data.success));
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	} finally {
		yield* put(fetchContestResultsClear());
	}
};

export const clearContestPointsSaga = function* (): SagaIterator {
	try {
		const contest = yield* select(getActiveContest);
		if (!contest) {
			throw new ApiError("Invalid contest");
		}

		const response = yield* call(Api.Contest.clearAnswers, contest.id);
		ApiError.CHECK(response.data);
		yield* put(clearContestPointsSuccess());
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	}
};

export const trackNewQuestionSaga = function* (questionIndexAdd = 0): SagaIterator {
	try {
		const gameBar = yield* select(getContestGameBar);
		const contest = yield* select(getActiveContest);
		const questionIndex = yield* select(getCurrentQuestionIndex);
		AdobeAnalyticService.TrackState({
			"page.name": AdobeTrackStateActions.PlayerStats,
			"page.viewType": "",
			"page.viewSubType": "",
			"page.subPage.index": questionIndex + questionIndexAdd,
			"gaming.mol.todayBest": gameBar?.bestToday ?? 0,
			"gaming.mol.gameResult": gameBar?.contestPoints ?? 0,
			"gaming.mol.personalBest": gameBar?.bestPersonal ?? 0,
			"gaming.dd.gameDate": AdobeAnalyticService.getTodayDateForAnalytics(),
			"gaming.mol.category": getLocallyTranslatedCategory(contest?.category as PlayerStats),
			"gaming.mol.timer": 10,
		});
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	}
};

interface IAnswerSagaProps {
	time: number;
	isCorrect: boolean;
}
export const answerResponseAnalyticSaga = function* ({
	time,
	isCorrect,
}: IAnswerSagaProps): SagaIterator {
	try {
		const gameBar = yield* select(getContestGameBar);
		const contest = yield* select(getActiveContest);
		const currentQuestionIndex = yield* select(getCurrentQuestionIndex);
		AdobeAnalyticService.TrackState({
			"page.name": isCorrect
				? AdobeTrackStateActions.PlayerStatsCorrect
				: AdobeTrackStateActions.PlayerStatsIncorrect,
			"page.subType": "player stat",
			"page.viewType": isCorrect ? "correct" : "incorrect",
			"page.viewSubType": "",
			"page.subPage.index": currentQuestionIndex,
			"gaming.mol.strikes": String(numOrZero(gameBar?.numberStrikes)),
			"gaming.mol.todayBest": numOrZero(gameBar?.bestToday),
			"gaming.mol.gameResult": numOrZero(gameBar?.contestPoints),
			"gaming.mol.personalBest": numOrZero(gameBar?.bestPersonal),
			"gaming.dd.gameDate": AdobeAnalyticService.getTodayDateForAnalytics(),
			"gaming.mol.category": getLocallyTranslatedCategory(contest?.category as PlayerStats),
			"gaming.mol.timer": time,
		});
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	}
};

export const gameOverModalAnalytics = function* (): SagaIterator {
	try {
		const activeContest = yield* select(getActiveContest);
		if (!activeContest) {
			return;
		}

		const response = yield* call(Api.Contest.showMy, activeContest.id);
		ApiError.CHECK(response.data);
		yield* put(fetchContestResultsSuccess(response.data.success));

		const correctAnswers = yield* select(getContestCorrectAnswers);

		AdobeAnalyticService.TrackState({
			"page.name": AdobeTrackStateActions.ResultsModal,
			"page.viewType": "",
			"page.viewSubType": "",
			"gaming.mol.gameResult": correctAnswers || 0,
			"gaming.dd.gameDate": AdobeAnalyticService.getTodayDateForAnalytics(),
			"gaming.mol.category": getLocallyTranslatedCategory(
				activeContest.category as PlayerStats
			),
		});
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	} finally {
		yield* put(fetchContestResultsClear());
	}
};
